import React, { useContext, useState, useRef } from 'react';
import classnames from 'clsx';
import styles from '../button.scss';
import { TestIds } from '../../../../constants';
import StylesContext from '../../../StylesContext';
import type { TextSizeBlockType } from './utils/utils';
import {
  textSizeBlockTypes,
  getActiveTextSize,
  getStateWithNewTextSize,
  preventBubblingUp,
} from './utils/utils';
import type { ExtendedToolbarChildrenProps } from '../../types';
import { useClickOutside } from '../../../../../../providers/useClickOutside/useClickOutside';
import { usePopper } from '../../../../../../providers/usePopper/usePopper';

export const textSizeButtons: Record<TextSizeBlockType, string> = {
  unstyled: 'Normal Text',
  'header-two': 'Title',
  'header-three': 'Subtitle',
};

export default (props: ExtendedToolbarChildrenProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const stylesContext = useContext(StylesContext);
  const { setEditorState, getEditorState, rootId } = props;

  const menuRef = useRef<HTMLDivElement>(null);

  const {
    ref: popperSourceElem,
    setRef: setPopperSourceElem,
    setPopper: setPopperTargetElem,
    styles: stylesPopper,
    attributes,
  } = usePopper<HTMLButtonElement>({
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -25],
        },
      },
    ],
  });

  const hideOptions = () => {
    setShowOptions(false);
  };

  useClickOutside([popperSourceElem], hideOptions);

  const changeTextSize = (
    e: React.MouseEvent<HTMLOrSVGElement>,
    newTextSize: string,
  ) => {
    preventBubblingUp(e);

    const newState = getStateWithNewTextSize(newTextSize, getEditorState());
    hideOptions();
    setEditorState(newState);
  };

  const onClickHandler = (event: React.MouseEvent<any>) => {
    preventBubblingUp(event);
    if (event.currentTarget === popperSourceElem) {
      setShowOptions(!showOptions);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      setShowOptions(false);
      popperSourceElem?.focus();
    }
  };

  const handleBlur = (event: React.FocusEvent) => {
    const relatedTarget = event.relatedTarget;
    if (
      relatedTarget &&
      menuRef.current &&
      menuRef.current.contains(relatedTarget)
    ) {
      return;
    }
    hideOptions();
  };

  const activeTextSize = getActiveTextSize(getEditorState());

  return (
    <div className={styles.buttonContainer} onMouseDown={preventBubblingUp}>
      <button
        type="button"
        ref={setPopperSourceElem}
        className={classnames(styles.button, styles.buttonTextContent, {
          [styles.hovered]: stylesContext.hovered,
          [styles.focused]: stylesContext.focused,
          [styles.error]: stylesContext.error,
        })}
        onClick={onClickHandler}
        data-testid={TestIds.textSizeButton}
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={showOptions}
        aria-controls={`textSizeMenu_${rootId}`}
      >
        {textSizeButtons[activeTextSize]}
      </button>
      <div
        ref={setPopperTargetElem}
        style={{
          ...stylesPopper.popper,
          visibility: showOptions ? 'visible' : 'hidden',
          zIndex: '1',
        }}
        {...attributes.popper}
        onClick={onClickHandler}
        role="menu"
        id={`textSizeMenu_${rootId}`}
      >
        <div
          ref={menuRef}
          className={classnames(styles.menuContainer, {
            [styles.hovered]: stylesContext.hovered,
            [styles.focused]: stylesContext.focused,
            [styles.error]: stylesContext.error,
          })}
          onKeyDown={onKeyDown}
          onBlur={handleBlur}
        >
          {textSizeBlockTypes.map(textSize => (
            <button
              key={textSize}
              onClick={e => changeTextSize(e, textSize)}
              className={classnames(
                styles.buttonTextContent,
                textSize === activeTextSize && styles.buttonTextSelected,
              )}
              data-testid={`${TestIds.textSizeButton}-${textSize}`}
              tabIndex={0}
              role="menuitemradio"
              aria-checked={activeTextSize === textSize}
            >
              {textSizeButtons[textSize]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
